import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Flex,
  Label,
  ModalV2,
  TypeaheadInput,
  type SearchOption,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { type AssignableEntity, useAssignableEntities } from "#hooks/useAssignableEntities";
import { fileIconType, type EffectiveDoc } from "#utils/classificationGrouping";
import { dateToIso, isoToDate } from "#utils/coverageGrid.utils";

export type ClassifyValues = {
  entityId: string;
  entityName: string;
  reportingDate: string;
};

export type ClassifyModalProps = {
  isOpen: boolean;
  mode: "assign" | "move";
  /** One or more documents to classify in one go. Move is always single; assign
   *  can be a bulk selection from the "Not classified" bucket. */
  docs: EffectiveDoc[];
  onClose: () => void;
  onConfirm: (values: ClassifyValues) => void;
  /** Arena the entity search is scoped to; undefined until the host app resolves
   *  it, which no-ops the search. */
  arenaId: number | undefined;
};

/**
 * ClassifyModal — assigns one or more unclassified documents to, or moves a
 * classified document between, deliverable packages. The entity picker searches
 * the caller's arena server-side via `useAssignableEntities`.
 *
 * It collects only the package-level attributes — entity and reporting date —
 * which are one value across the whole selection by definition. Document type
 * describes each file individually and is edited per document through the Edit
 * document type action; collecting it here applied a single type to every
 * selected document and destroyed their classifications. Package type is fixed
 * to "Period close" and has no selector.
 */
export const ClassifyModal: React.FC<ClassifyModalProps> = ({
  isOpen,
  mode,
  docs,
  onClose,
  onConfirm,
  arenaId,
}) => {
  const { search, options, loading } = useAssignableEntities(arenaId);
  const [selectedEntity, setSelectedEntity] = useState<AssignableEntity | null>(null);
  const [reportingDate, setReportingDate] = useState<Date | null>(null);

  const primaryDoc = docs[0] ?? null;
  const isBulk = docs.length > 1;

  useEffect(() => {
    if (!isOpen || !primaryDoc) {
      return;
    }
    // Move preselects the document's current entity; the read model carries the
    // name, so the picker can display it without a search round-trip. Assign
    // starts blank.
    setSelectedEntity(
      mode === "move" && primaryDoc.entityId
        ? { id: primaryDoc.entityId, name: primaryDoc.entityName ?? primaryDoc.entityId }
        : null,
    );
    setReportingDate(primaryDoc.reportingDate ? isoToDate(primaryDoc.reportingDate) : null);
  }, [isOpen, primaryDoc, mode]);

  const entityOptions = useMemo<SearchOption[]>(
    () => options.map((e) => ({ id: e.id, label: e.name })),
    [options],
  );

  if (!primaryDoc) {
    return null;
  }

  const title =
    mode === "move" ? "Move to a deliverable package" : "Assign to a deliverable package";
  const canConfirm = !!selectedEntity && !!reportingDate;

  const handleConfirm = () => {
    if (!selectedEntity || !reportingDate) {
      return;
    }
    onConfirm({
      entityId: selectedEntity.id,
      entityName: selectedEntity.name,
      // The picked date is preserved as-is (no quarter-end snap): a snapped date
      // would disagree with the date printed in the document and trip the check
      // engine. The coverage grid grows a column for whatever reporting date a
      // package carries, so a mid-quarter date still lands on its own column.
      reportingDate: dateToIso(reportingDate),
    });
  };

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      size="medium"
      title={title}
      description={
        isBulk ? (
          `${docs.length} documents`
        ) : (
          <Flex inline alignItems="center" gap={6}>
            <Icon type={fileIconType(primaryDoc.filename)} variant="solid" aria-hidden="true" />
            {primaryDoc.filename}
          </Flex>
        )
      }
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={16}>
          <Flex flexDirection="column" gap={6}>
            {/* Keyed by the document so move-mode reopens remount the input and
                re-seed the current entity name via initialSearchValue; the DS
                input owns its text value, so a remount is how we preselect. */}
            <TypeaheadInput
              key={primaryDoc.id}
              isFullWidth
              isClearable
              isLoading={loading}
              label="Entity"
              placeholder="Search for an entity…"
              options={entityOptions}
              initialSearchValue={selectedEntity?.name ?? ""}
              minLength={2}
              onDebouncedSearch={search}
              onSelectOption={(option) =>
                setSelectedEntity({ id: String(option.id), name: option.label })
              }
              onClearValue={() => setSelectedEntity(null)}
            />
          </Flex>
          <Flex flexDirection="column" gap={6}>
            <Label>Reporting date</Label>
            <DatePicker
              hasIcon
              isFull
              selected={reportingDate}
              onChange={(d: Date | null) => setReportingDate(d)}
              dateFormat="MMM d, yyyy"
            />
          </Flex>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={8}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!canConfirm}>
            Confirm
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
